import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Target, AlertTriangle, Shield, User, XCircle, Clock, BarChart3, CheckCircle2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import StatCard from '@/components/dashboard/StatCard';
import CertExpiryChart from '@/components/reports/CertExpiryChart';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

export default function ComplianceDashboardPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [renewalReport, setRenewalReport] = useState(null);
  const [certRenewals, setCertRenewals] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, trackRes, renewRes, certRes] = await Promise.all([
        fetch(`${API_URL}/api/compliance/dashboard`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/compliance/mandatory-tracking`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/training/renewal-report`, { headers: getAuthHeaders() }).catch(() => null),
        fetch(`${API_URL}/api/auditor/certificate-renewals?days=90`, { headers: getAuthHeaders() }).catch(() => null),
      ]);
      if (dashRes.ok) setData(await dashRes.json());
      if (trackRes.ok) setTracking(await trackRes.json());
      if (renewRes?.ok) setRenewalReport(await renewRes.json());
      if (certRes?.ok) setCertRenewals(await certRes.json());
    } catch (err) {
      toast.error('Veriler yüklenirken hata');
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (score) => score >= 70 ? 'text-red-500' : score >= 40 ? 'text-amber-500' : 'text-emerald-500';
  const getRiskBg = (score) => score >= 70 ? 'bg-red-500/10 border-red-500/20' : score >= 40 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-emerald-500/10 border-emerald-500/20';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Target className="w-10 h-10 text-primary animate-pulse" />
          <p className="text-muted-foreground text-sm">Risk verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const riskScore = data?.risk_score || 0;

  const riskColor = riskScore >= 70 ? 'red' : riskScore >= 40 ? 'amber' : 'emerald';

  return (
    <div className="" data-testid="compliance-dashboard">
      <DashboardHeader
        icon={Shield}
        title="Uyum Görevlisi Paneli"
        subtitle="Zorunlu Eğitim Takibi & Risk Yönetimi"
        onRefresh={loadData}
        actions={<Badge variant="outline"><User className="w-3 h-3 mr-1" />{currentUser?.name}</Badge>}
      />

      <main className="container mx-auto px-4 py-6 max-w-7xl space-y-6">
        {/* Risk Score + Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard color={riskColor} value={riskScore} label="Risk Skoru" />
          <StatCard color="primary" value={summary.total_users || 0} label="Toplam Personel" />
          <StatCard color="emerald" value={summary.compliant || 0} label="Uyumlu" />
          <StatCard color="amber" value={summary.partial || 0} label="Kısmi Uyumlu" />
          <StatCard color="red" value={summary.non_compliant || 0} label="Uyumsuz" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* High Risk Users */}
          <Card data-testid="high-risk-users">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />Yüksek Riskli Personel
                <Badge variant="destructive" className="ml-auto">{data?.high_risk_users?.length || 0}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[300px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Personel</TableHead><TableHead>Departman</TableHead><TableHead className="text-center">Tamamlama</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data?.high_risk_users || []).slice(0, 15).map((u, i) => (
                      <TableRow key={i}>
                        <TableCell><div className="text-sm font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{u.department || 'Genel'}</Badge></TableCell>
                        <TableCell className="text-center"><span className="text-sm font-medium text-red-500">%{u.completion || 0}</span></TableCell>
                      </TableRow>
                    ))}
                    {(!data?.high_risk_users || data.high_risk_users.length === 0) && (
                      <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground"><CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-500" />Yüksek riskli personel yok</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Department Risk Distribution */}
          <Card data-testid="dept-risks">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4" />Departman Risk Dağılımı</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(data?.department_risks || []).map((dept, i) => (
                  <div key={i} className={`p-3 rounded-lg border ${getRiskBg(dept.risk_score || 0)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{dept.department}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${getRiskColor(dept.risk_score || 0)}`}>{dept.risk_score || 0}</span>
                        <Badge variant="outline" className="text-xs">{dept.user_count || 0} kişi</Badge>
                      </div>
                    </div>
                    <Progress value={100 - (dept.risk_score || 0)} className="h-1.5" />
                    <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
                      <span>Uyumlu: {dept.compliant || 0}</span>
                      <span>Uyumsuz: {dept.non_compliant || 0}</span>
                    </div>
                  </div>
                ))}
                {(!data?.department_risks || data.department_risks.length === 0) && (
                  <p className="text-center text-muted-foreground py-4">Departman verisi yok</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mandatory Training Tracking */}
        {tracking?.departments && (
          <Card data-testid="mandatory-tracking">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4" />Zorunlu Eğitim Tamamlanma Oranları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {tracking.departments.map((dept, i) => (
                  <div key={i} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium truncate">{dept.department}</span>
                      <span className="text-sm font-bold">%{dept.compliance_rate || 0}</span>
                    </div>
                    <Progress value={dept.compliance_rate || 0} className="h-2 mb-1" />
                    <div className="text-[10px] text-muted-foreground">{dept.completed || 0}/{dept.total || 0} zorunlu eğitim</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Overdue Trainings */}
        {data?.overdue_trainings?.length > 0 && (
          <Card className="border-amber-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-amber-500" />Geciken Eğitimler<Badge className="bg-amber-500/15 text-amber-500 ml-auto">{data.overdue_trainings.length}</Badge></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.overdue_trainings.slice(0, 10).map((t, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                    <div><span className="font-medium">{t.user_name}</span><span className="text-muted-foreground ml-2">- {t.training_name}</span></div>
                    <Badge variant="outline" className="text-amber-500">{t.days_overdue} gün gecikme</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sertifika Yenileme Dağılımı (30/60/90) */}
        {certRenewals?.summary && <CertExpiryChart summary={certRenewals.summary} />}

        {/* Training Renewal Report */}
        {renewalReport && (renewalReport.summary?.total_overdue > 0 || renewalReport.summary?.total_expiring > 0) && (
          <Card data-testid="renewal-report">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-red-500" />Periyot Yenileme Raporu
                <Badge variant="destructive" className="ml-auto">{renewalReport.summary.total_overdue} suresi dolmus</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-500">{renewalReport.summary.total_overdue}</div>
                  <div className="text-xs text-muted-foreground">Suresi Dolmus Egitim</div>
                </div>
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-500">{renewalReport.summary.total_expiring}</div>
                  <div className="text-xs text-muted-foreground">30 Gun Icinde Dolacak</div>
                </div>
              </div>

              {/* Overdue List */}
              {renewalReport.overdue?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-red-500">Suresi Dolmus (ilk 20)</h4>
                  <div className="max-h-[250px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Personel</TableHead>
                          <TableHead>Departman</TableHead>
                          <TableHead>Modul</TableHead>
                          <TableHead className="text-center">Durum</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {renewalReport.overdue.slice(0, 20).map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <div className="text-sm font-medium">{r.user_name}</div>
                              <div className="text-xs text-muted-foreground">{r.email}</div>
                            </TableCell>
                            <TableCell><Badge variant="outline" className="text-xs">{r.department || 'Genel'}</Badge></TableCell>
                            <TableCell className="text-xs">{r.module_name?.substring(0, 25)}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="destructive" className="text-xs">
                                {r.status === 'not_started' ? 'Baslanmamis' : `${r.days_overdue} gun gecikme`}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Expiring Soon */}
              {renewalReport.expiring_soon?.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2 text-amber-500">30 Gun Icinde Dolacak</h4>
                  <div className="space-y-1">
                    {renewalReport.expiring_soon.slice(0, 10).map((r, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-amber-500/5 rounded text-sm">
                        <span>{r.user_name} - {r.module_name?.substring(0, 25)}</span>
                        <Badge variant="outline" className="text-amber-500 text-xs">{r.days_remaining} gun</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
